import { Card, CardContent, Stack, Typography } from '@mui/joy';
import { Check, Copy } from '@phosphor-icons/react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Confirmation } from 'core/components';
import { Color, Level, Variant } from 'theme/enums';

interface IProps {
  errorMessage: string;
  // Defaults describe a failed order; a status where the order has not failed (Submission Timed
  // Out) passes its own title and colour so the dialog does not tell the user it failed.
  title?: string;
  color?: Color;
}

export const ExternalOrderErrorMessageButton = ({
  errorMessage,
  title,
  color = Color.DANGER,
}: IProps) => {
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
  const [isCopied, setIsCopied] = useState(false);

  const { t } = useTranslation();

  const onOpenConfirmation = () => {
    setIsConfirmationOpen(true);
  };

  const onCloseConfirmation = () => {
    setIsConfirmationOpen(false);
    setIsCopied(false);
  };

  if (!errorMessage) {
    return null;
  }

  return (
    <>
      <Typography
        color={Color.PRIMARY}
        onClick={onOpenConfirmation}
        sx={{
          fontSize: '12px !important',
          fontWeight: '500 !important',
          cursor: 'pointer',

          '&:hover': {
            fontWeight: '600 !important',
          },
        }}
      >
        {t('buttonLabelSeeErrorMessage')}
      </Typography>

      <Confirmation
        isOpen={isConfirmationOpen}
        title={title || t('titleOrderFailedDetails')}
        color={color}
        message={<ErrorMessageContent errorMessage={errorMessage} color={color} />}
        onCancel={onCloseConfirmation}
        cancelBtnColor={Color.PRIMARY}
        cancelBtnLabel={t('buttonLabelClose')}
        cancelBtnVariant={Variant.SOLID}
        extraButtons={[
          <Button
            key="copyButton"
            color={isCopied ? Color.SUCCESS : Color.NEUTRAL}
            variant={isCopied ? Variant.SOFT : Variant.OUTLINED}
            text={isCopied ? t('buttonLabelCopied') : t('buttonLabelCopy')}
            startDecorator={isCopied ? <Check /> : <Copy />}
            onClick={() => {
              navigator.clipboard.writeText(errorMessage);

              setIsCopied(true);

              setTimeout(onCloseConfirmation, 300);
            }}
          />,
        ]}
      />
    </>
  );
};

const ErrorMessageContent = ({ errorMessage, color }: { errorMessage: string; color: Color }) => {
  const { t } = useTranslation();

  const formattedMessage = errorMessage
    ? errorMessage.replace(/--/g, '\n--')
    : t('labelNoErrorMessage');

  return (
    <Stack gap={1}>
      <Typography level={Level.TITLE_MD}>{t('labelErrorMessage')}</Typography>

      <Card color={color} variant={Variant.SOFT}>
        <CardContent sx={{ whiteSpace: 'pre-wrap' }}>{formattedMessage}</CardContent>
      </Card>
    </Stack>
  );
};
